import { useQuery } from '@tanstack/react-query';
import { listStaffRoles } from '@littlebooker/dal';
import { X } from 'lucide-react';

interface RoleMultiSelectProps {
  value: string[];
  onChange: (roleIds: string[]) => void;
}

/**
 * Chip-based multiselect for staff roles. Reads the account's role catalog and
 * lets the user toggle which roles apply. Used for person qualifications.
 */
export function RoleMultiSelect({ value, onChange }: RoleMultiSelectProps) {
  const { data: roles = [], isLoading } = useQuery({
    queryKey: ['staff-roles'],
    queryFn: () => listStaffRoles(),
  });

  const toggle = (id: string) => {
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  };

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading roles…</p>;
  if (roles.length === 0)
    return (
      <p className="text-sm text-muted-foreground">
        No roles defined yet. Add some under Settings → Staff Roles.
      </p>
    );

  return (
    <div className="flex flex-wrap gap-2">
      {roles.map((role) => {
        const selected = value.includes(role.id);
        return (
          <button
            key={role.id}
            type="button"
            onClick={() => toggle(role.id)}
            className={
              selected
                ? 'inline-flex items-center gap-1 rounded-full border border-primary bg-primary/15 px-3 py-1 text-sm text-primary'
                : 'inline-flex items-center gap-1 rounded-full border border-input px-3 py-1 text-sm text-muted-foreground hover:bg-accent'
            }
          >
            {role.name}
            {selected && <X className="h-3 w-3" />}
          </button>
        );
      })}
    </div>
  );
}
